import { useState, useCallback, useMemo } from 'react';

const IGNORE_RESET_MS = 600;

/**
 * Hook for chat model/provider selector state.
 * State lives in context (via HomeChatProviderWrapper), so selection persists across AgentView / ConversationView.
 * Ignores a spurious onModelChange to the default (first) model when it happens right after send,
 * to avoid ChatInput (in the newly mounted ConversationView) resetting the selection.
 *
 * @param {Array} providersList - Array of { provider, label, models: [{ value, label }] }
 * @param {{ lastSendTimeRef?: React.MutableRefObject<number | null> }} options - Optional. lastSendTimeRef: ref updated with Date.now() when a message is sent.
 * @returns {Object} { providers, selectedProvider, selectedModel, onModelChange, onProviderChange }
 */
export const useChatModelSelector = (providersList = [], options = {}) => {
  const { lastSendTimeRef } = options;
  const providers = Array.isArray(providersList) ? providersList : [];

  const firstProvider = providers[0];
  const firstModel = firstProvider?.models?.[0]?.value;

  const [selectedProvider, setSelectedProvider] = useState(
    firstProvider?.provider ?? null
  );
  const [selectedModel, setSelectedModel] = useState(firstModel ?? null);

  const onProviderChange = useCallback((provider) => {
    setSelectedProvider(provider);
    const providerEntry = providers.find((p) => p.provider === provider);
    const firstModelOfProvider = providerEntry?.models?.[0]?.value ?? null;
    setSelectedModel(firstModelOfProvider);
  }, [providers]);

  const onModelChange = useCallback(
    (modelValue) => {
      setSelectedModel((prev) => {
        if (prev === modelValue) return prev;
        // Evitar reset espurio: al enviar el primer mensaje se monta ConversationView y su ChatInput
        // puede llamar onModelChange con el valor por defecto (primer modelo). Ignorar ese cambio
        // si ocurre justo después de enviar.
        if (
          lastSendTimeRef?.current != null &&
          firstModel != null &&
          modelValue === firstModel &&
          prev !== firstModel
        ) {
          const elapsed = Date.now() - lastSendTimeRef.current;
          if (elapsed < IGNORE_RESET_MS) return prev;
        }
        return modelValue;
      });
    },
    [firstModel, lastSendTimeRef]
  );

  return useMemo(
    () => ({
      providers,
      selectedProvider,
      selectedModel,
      onModelChange,
      onProviderChange,
    }),
    [
      providers,
      selectedProvider,
      selectedModel,
      onModelChange,
      onProviderChange,
    ]
  );
};
